import { useParams } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumb from '@/components/Breadcrumb';
import ArticleCard from '@/components/ArticleCard';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { getCategoryById } from '@/mocks/categories';
import { getCommentsByArticleId } from '@/mocks/comments';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-20">
          <div className="text-center">
            <i className="ri-file-close-line text-5xl text-foreground-300" />
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mt-4">文章未找到</h1>
            <p className="mt-2 text-sm text-foreground-500">该文章不存在或已被移除</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = getCategoryById(article.categoryId);
  const relatedArticles = getRelatedArticles(article.id, article.categoryId, 3);
  const articleComments = getCommentsByArticleId(article.id);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.categoryName, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.image,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '猎奇吃瓜',
      logo: {
        '@type': 'ImageObject',
        url: 'http://odki7b.com/logo.png',
      },
    },
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    url: `http://odki7b.com/article/${article.id}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://odki7b.com/article/${article.id}`,
    },
  };

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: article.title,
    description: article.summary,
    url: `http://odki7b.com/article/${article.id}`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://odki7b.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.categoryName,
        item: `http://odki7b.com/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: `http://odki7b.com/article/${article.id}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 猎奇吃瓜`}
        description={article.summary}
        keywords={`${article.tags.join(',')},猎奇吃瓜,新鲜吃瓜八卦,冷门趣味爆料,无压力围观趣事`}
        canonical={`http://odki7b.com/article/${article.id}`}
        ogImage={article.image}
        ogType="article"
        schema={[articleSchema, pageSchema, breadcrumbSchema]}
      />

      <Navbar />

      <main>
        {/* Article Hero */}
        <section className="relative min-h-[420px] md:min-h-[500px] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
          </div>

          <div className="relative w-full px-4 md:px-6 lg:px-10 pb-8 md:pb-12 pt-24 md:pt-32">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-white">
                {article.categoryName}
              </span>
              <span className="text-xs text-white/70">{article.publishDate}</span>
            </div>
            <h1 className="font-heading text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white max-w-4xl leading-tight">
              {article.title}
            </h1>
            <p className="mt-3 text-sm md:text-base text-white/80 max-w-2xl line-clamp-2">
              {article.summary}
            </p>
          </div>
        </section>

        {/* Author & Stats */}
        <section className="bg-background-50 py-8 md:py-12">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
              <div className="lg:col-span-3">
                <span className="text-xs font-medium text-foreground-500 border border-foreground-300 px-2 py-1 rounded">
                  关于本文
                </span>
                <div className="mt-4">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                    作者档案
                  </h2>
                  <p className="text-foreground-400 text-lg">{article.author}</p>
                </div>
                <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                  {article.author}是猎奇吃瓜的特约撰稿人，专注于{article.categoryName}领域的深度挖掘，
                  致力于为读者带来最新鲜、最真实的娱乐资讯与猎奇故事。
                </p>
                <div className="flex items-center gap-4 mt-4">
                  <div className="text-center">
                    <p className="text-2xl md:text-3xl font-bold text-foreground-900">{formatNumber(article.readCount)}</p>
                    <p className="text-xs text-foreground-500">阅读量</p>
                  </div>
                  <div className="w-px h-10 bg-background-300" />
                  <div className="text-center">
                    <p className="text-2xl md:text-3xl font-bold text-foreground-900">{formatNumber(article.likeCount)}</p>
                    <p className="text-xs text-foreground-500">获赞</p>
                  </div>
                  <div className="w-px h-10 bg-background-300" />
                  <div className="text-center">
                    <p className="text-2xl md:text-3xl font-bold text-foreground-900">{formatNumber(article.commentCount)}</p>
                    <p className="text-xs text-foreground-500">评论</p>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-2 flex gap-3 justify-end">
                <div className="w-28 h-28 md:w-32 md:h-32 rounded-xl overflow-hidden shadow-md shrink-0">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="w-36 h-44 md:w-44 md:h-56 rounded-xl overflow-hidden shadow-md -mt-4">
                  <img
                    src={article.image}
                    alt="相关场景"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="bg-background-50 py-6 md:py-10">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-3xl mx-auto">
              <article className="prose prose-sm md:prose-base max-w-none">
                <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
                  <p className="text-lg md:text-xl font-medium text-foreground-900">
                    {article.summary}
                  </p>
                  <p>
                    近日，关于此事件的消息在网络上引发了广泛讨论。据多方消息源透露，事件的核心细节正在逐步浮出水面。
                    从目前的线索来看，这背后可能隐藏着更多不为人知的内幕。
                  </p>
                  <p>
                    知情人士表示，事情的发展远比表面看上去更加复杂。多名相关人士在接受匿名采访时透露，
                    这一事件已经引起了业内人士的高度关注，不少人正在密切关注事态的进一步发展。
                  </p>
                  <div className="my-6 p-4 md:p-5 bg-accent-50 rounded-xl border-l-4 border-accent-400">
                    <p className="text-sm md:text-base text-foreground-700 italic">
                      "我们只是把大家想知道的事情说出来，至于真相如何，每个人心中都有自己的判断。"
                      —— 某业内人士透露
                    </p>
                  </div>
                  <p>
                    随着更多细节的曝光，网友们也纷纷发表了自己的看法。有人认为这是"贵圈日常"，
                    也有人表示"希望当事人能够给出正面回应"。无论如何，这一事件已经成为了当下最热门的话题之一。
                  </p>
                  <p>
                    值得注意的是，类似的爆料在过去也时有发生，但能够引起如此大范围关注的并不多见。
                    这或许说明，公众对于透明度和真实性的需求正在不断提高。
                  </p>
                  <div className="my-6">
                    <img
                      src={article.image}
                      alt="文章配图"
                      className="w-full rounded-xl"
                    />
                    <p className="text-xs text-foreground-400 mt-2 text-center">
                      事件相关场景
                    </p>
                  </div>
                  <p>
                    截至目前，相关各方尚未对此事做出正式回应。我们将持续关注事态发展，为读者带来最新的后续报道。
                    如果您有任何线索或独家消息，欢迎通过我们的爆料通道与我们取得联系。
                  </p>
                </div>
              </article>

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200">
                <span className="text-xs text-foreground-400">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-600 border border-background-200"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Comments Section */}
        {articleComments.length > 0 && (
          <section className="bg-background-100 py-10 md:py-14">
            <div className="w-full px-4 md:px-6 lg:px-10">
              <div className="mb-6">
                <span className="inline-block px-3 py-1 rounded-lg bg-secondary-200 text-secondary-800 text-xs font-medium">
                  围观热评
                </span>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-3">
                  吃瓜网友 · 神回复
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {articleComments.map((comment) => (
                  <div
                    key={comment.id}
                    className="bg-background-50 rounded-xl p-4 border border-background-200"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={comment.avatar}
                        alt={comment.nickname}
                        className="w-9 h-9 rounded-full object-cover"
                        loading="lazy"
                      />
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{comment.nickname}</p>
                        <p className="text-xs text-foreground-400">@{comment.account}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      「{comment.content}」
                    </p>
                    <div className="flex items-center gap-1 mt-2.5 text-xs text-foreground-400">
                      <i className="ri-heart-line" />
                      <span>{comment.likes}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-background-50 py-10 md:py-14">
            <div className="w-full px-4 md:px-6 lg:px-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                猜你喜欢
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} variant="horizontal" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}

function formatNumber(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}w`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}